import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { upsertFeedback } from "../ai-reports-service";

const REASONS = [
    { value: "wrong_data", label: "Wrong or inaccurate data" },
    { value: "bad_visualization", label: "Wrong chart type or visualization" },
    { value: "irrelevant", label: "Doesn't answer my question" },
    { value: "other", label: "Something else" },
];

const iconSx = { fontSize: 16 };

const FeedbackButtons = ({ targetType, targetId, initialVote = null, onVoteChange }) => {
    const [vote, setVote] = useState(initialVote);
    const [pending, setPending] = useState(false);
    const [reasonAnchor, setReasonAnchor] = useState(null);

    const submit = async (value, reason = undefined) => {
        if (pending) return true;
        const prev = vote;
        setVote(value);
        onVoteChange?.(value);
        setPending(true);
        try {
            await upsertFeedback(targetType, targetId, value, reason);
            return true;
        } catch {
            setVote(prev);
            onVoteChange?.(prev);
            return false;
        } finally {
            setPending(false);
        }
    };

    const handleThumbUp = () => {
        if (vote === 1 || pending) return;
        submit(1);
    };

    const handleThumbDown = async (e) => {
        if (pending) return;
        const anchor = e.currentTarget;
        if (vote !== -1) {
            const ok = await submit(-1);
            if (!ok) return;
        }
        setReasonAnchor(anchor);
    };

    const handleReasonSelect = (reason) => {
        setReasonAnchor(null);
        submit(-1, reason);
    };

    return (
        <Box display="flex" alignItems="center">
            <Tooltip title="Helpful">
                <span>
                    <IconButton
                        size="small"
                        onClick={handleThumbUp}
                        disabled={pending}
                        sx={{
                            p: 0.5,
                            color: vote === 1 ? "success.main" : "text.disabled",
                            "&:hover": { color: "success.main" },
                        }}
                    >
                        {vote === 1 ? (
                            <ThumbUpIcon sx={iconSx} />
                        ) : (
                            <ThumbUpOutlinedIcon sx={iconSx} />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Not helpful">
                <span>
                    <IconButton
                        size="small"
                        onClick={handleThumbDown}
                        disabled={pending}
                        sx={{
                            p: 0.5,
                            color: vote === -1 ? "error.main" : "text.disabled",
                            "&:hover": { color: "error.main" },
                        }}
                    >
                        {vote === -1 ? (
                            <ThumbDownIcon sx={iconSx} />
                        ) : (
                            <ThumbDownOutlinedIcon sx={iconSx} />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
            <Menu
                anchorEl={reasonAnchor}
                open={!!reasonAnchor}
                onClose={() => setReasonAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <MenuItem disabled dense>
                    <Typography variant="caption" color="text.secondary">
                        What went wrong? (optional)
                    </Typography>
                </MenuItem>
                {REASONS.map((r) => (
                    <MenuItem key={r.value} onClick={() => handleReasonSelect(r.value)} dense>
                        {r.label}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default FeedbackButtons;
